import React, { useEffect, useMemo, useState } from 'react';
import {
  IonBadge,
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonCheckbox,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonRadio,
  IonRadioGroup,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTextarea,
  IonToast,
  IonToggle
} from '@ionic/react';
import { addOutline, saveOutline, trashOutline } from 'ionicons/icons';
import {
  aggregateSurveyResults,
  createEmptySessionSurvey,
  createSurveyQuestion,
  SessionSurveyConfig,
  SessionSurveyQuestion,
  SessionSurveyResponse,
  sessionSurveyService,
  SurveyAnswer,
  SurveyQuestionType,
  validateSessionSurvey,
  validateSurveyAnswers
} from '../services/SessionSurveyService';
import './SessionSurvey.css';

interface SessionSurveyProps {
  eventCode: string;
  sessionId: string | number;
  survey: SessionSurveyConfig | null;
  isLogisticSupport: boolean;
}

const cloneSurvey = (survey: SessionSurveyConfig): SessionSurveyConfig => ({
  ...survey,
  questions: survey.questions.map(question => ({
    ...question,
    options: [...question.options]
  }))
});

const SessionSurvey: React.FC<SessionSurveyProps> = ({
  eventCode,
  sessionId,
  survey,
  isLogisticSupport
}) => {
  const [draft, setDraft] = useState<SessionSurveyConfig>(() =>
    createEmptySessionSurvey(eventCode, sessionId)
  );
  const [answers, setAnswers] = useState<Record<string, SurveyAnswer>>({});
  const [responses, setResponses] = useState<SessionSurveyResponse[]>([]);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [loadedResponseContext, setLoadedResponseContext] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const responseContext = `${eventCode}::${String(sessionId)}`;
  const isLoadingOwnResponse = !isLogisticSupport && Boolean(survey?.active) &&
    loadedResponseContext !== responseContext;
  const isResponseLocked = hasSubmitted || isLoadingOwnResponse || isSubmitting;

  useEffect(() => {
    setDraft(cloneSurvey(survey || createEmptySessionSurvey(eventCode, sessionId)));
  }, [survey, eventCode, sessionId]);

  useEffect(() => {
    if (isLogisticSupport || !survey?.active) {
      setLoadedResponseContext('');
      return;
    }

    setLoadedResponseContext('');
    setHasSubmitted(false);
    setAnswers({});
    return sessionSurveyService.subscribeOwnResponse(eventCode, sessionId, response => {
      setHasSubmitted(Boolean(response));
      setAnswers(response?.answers || {});
      setLoadedResponseContext(responseContext);
    }, error => {
      console.error('No se pudo cargar la respuesta de la encuesta:', error);
      setToastMessage('No se pudo verificar si ya respondió. Intente nuevamente.');
    });
  }, [eventCode, sessionId, survey?.active, isLogisticSupport, responseContext]);

  useEffect(() => {
    if (!isLogisticSupport) return;
    return sessionSurveyService.subscribeResponses(eventCode, sessionId, setResponses, error => {
      console.error('No se pudieron cargar los resultados de la encuesta:', error);
      setToastMessage('No se pudieron cargar los resultados.');
    });
  }, [eventCode, sessionId, isLogisticSupport]);

  const results = useMemo(
    () => survey ? aggregateSurveyResults(survey, responses) : [],
    [survey, responses]
  );

  const updateQuestion = (questionId: string, changes: Partial<SessionSurveyQuestion>) => {
    setDraft(current => ({
      ...current,
      questions: current.questions.map(question =>
        question.id === questionId ? { ...question, ...changes } : question
      )
    }));
  };

  const changeQuestionType = (questionId: string, type: SurveyQuestionType) => {
    updateQuestion(questionId, {
      type,
      options: type === 'text' ? [] : ['', '']
    });
  };

  const updateOption = (questionId: string, optionIndex: number, value: string) => {
    const question = draft.questions.find(item => item.id === questionId);
    if (!question) return;
    const options = [...question.options];
    options[optionIndex] = value;
    updateQuestion(questionId, { options });
  };

  const saveConfiguration = async () => {
    const errors = validateSessionSurvey(draft);
    if (errors.length > 0) {
      setToastMessage(errors[0]);
      return;
    }
    setIsSaving(true);
    try {
      await sessionSurveyService.saveSurvey(draft);
      setToastMessage(draft.active ? 'Encuesta guardada y activa.' : 'Configuración guardada.');
    } catch (error) {
      console.error('No se pudo guardar la encuesta:', error);
      setToastMessage(error instanceof Error ? error.message : 'No se pudo guardar la encuesta.');
    } finally {
      setIsSaving(false);
    }
  };

  const setAnswer = (questionId: string, value: SurveyAnswer) => {
    if (isResponseLocked) return;
    setAnswers(current => ({ ...current, [questionId]: value }));
  };

  const toggleMultipleAnswer = (questionId: string, option: string, checked: boolean) => {
    if (isResponseLocked) return;
    const selected = Array.isArray(answers[questionId]) ? answers[questionId] as string[] : [];
    setAnswer(
      questionId,
      checked ? Array.from(new Set([...selected, option])) : selected.filter(value => value !== option)
    );
  };

  const submitAnswers = async () => {
    if (!survey) return;
    if (hasSubmitted) {
      setToastMessage('Esta encuesta ya fue respondida y no puede modificarse.');
      return;
    }
    if (isLoadingOwnResponse) {
      setToastMessage('Espere mientras verificamos su respuesta.');
      return;
    }
    const errors = validateSurveyAnswers(survey, answers);
    if (errors.length > 0) {
      setToastMessage(errors[0]);
      return;
    }
    setIsSubmitting(true);
    try {
      await sessionSurveyService.submitResponse(survey, answers);
      setHasSubmitted(true);
      setToastMessage('Sus respuestas fueron guardadas.');
    } catch (error) {
      console.error('No se pudo enviar la encuesta:', error);
      if (error instanceof Error && error.message === 'SURVEY_ALREADY_SUBMITTED') {
        setHasSubmitted(true);
        setToastMessage('Esta encuesta ya fue respondida y no puede modificarse.');
      } else if (error instanceof Error && error.message === 'SURVEY_NOT_ACTIVE') {
        setToastMessage('La encuesta fue desactivada y ya no admite respuestas.');
      } else {
        setToastMessage(error instanceof Error ? error.message : 'No se pudo enviar la encuesta.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLogisticSupport) {
    return (
      <div className="session-survey">
        <IonCard className="survey-admin-card">
          <IonCardHeader>
            <div className="survey-card-heading">
              <IonCardTitle>Configurar encuesta</IonCardTitle>
              <IonBadge color={draft.active ? 'success' : 'medium'}>
                {draft.active ? 'Activa' : 'Inactiva'}
              </IonBadge>
            </div>
          </IonCardHeader>
          <IonCardContent>
            <IonItem lines="full">
              <IonLabel position="stacked">Título</IonLabel>
              <IonInput
                value={draft.title}
                maxlength={120}
                onIonChange={event => setDraft(current => ({
                  ...current,
                  title: event.detail.value || ''
                }))}
              />
            </IonItem>
            <IonItem lines="full">
              <IonLabel position="stacked">Descripción</IonLabel>
              <IonTextarea
                value={draft.description}
                maxlength={500}
                autoGrow
                onIonChange={event => setDraft(current => ({
                  ...current,
                  description: event.detail.value || ''
                }))}
              />
            </IonItem>
            <IonItem lines="none" className="survey-active-toggle">
              <IonLabel>
                <h3>Disponible para participantes</h3>
                <p>Al activarla aparecerá la pestaña Encuesta para los demás roles.</p>
              </IonLabel>
              <IonToggle
                checked={draft.active}
                onIonChange={event => setDraft(current => ({
                  ...current,
                  active: event.detail.checked
                }))}
              />
            </IonItem>

            <div className="survey-question-editor-list">
              {draft.questions.map((question, questionIndex) => (
                <div className="survey-question-editor" key={question.id}>
                  <div className="survey-question-editor-heading">
                    <strong>Pregunta {questionIndex + 1}</strong>
                    <IonButton
                      fill="clear"
                      color="danger"
                      size="small"
                      aria-label={`Eliminar pregunta ${questionIndex + 1}`}
                      onClick={() => setDraft(current => ({
                        ...current,
                        questions: current.questions.filter(item => item.id !== question.id)
                      }))}
                    >
                      <IonIcon icon={trashOutline} />
                    </IonButton>
                  </div>
                  <IonItem lines="full">
                    <IonLabel position="stacked">Texto de la pregunta</IonLabel>
                    <IonTextarea
                      value={question.prompt}
                      maxlength={250}
                      autoGrow
                      onIonChange={event => updateQuestion(question.id, {
                        prompt: event.detail.value || ''
                      })}
                    />
                  </IonItem>
                  <IonItem lines="full">
                    <IonLabel position="stacked">Tipo de respuesta</IonLabel>
                    <IonSelect
                      value={question.type}
                      interface="popover"
                      onIonChange={event => changeQuestionType(question.id, event.detail.value)}
                    >
                      <IonSelectOption value="single">Una opción</IonSelectOption>
                      <IonSelectOption value="multiple">Varias opciones</IonSelectOption>
                      <IonSelectOption value="text">Texto libre</IonSelectOption>
                    </IonSelect>
                  </IonItem>
                  <IonItem lines="none">
                    <IonLabel>Respuesta obligatoria</IonLabel>
                    <IonToggle
                      checked={question.required}
                      onIonChange={event => updateQuestion(question.id, {
                        required: event.detail.checked
                      })}
                    />
                  </IonItem>

                  {question.type !== 'text' && (
                    <div className="survey-options-editor">
                      <IonNote>Opciones</IonNote>
                      {question.options.map((option, optionIndex) => (
                        <div className="survey-option-editor" key={`${question.id}_${optionIndex}`}>
                          <IonInput
                            value={option}
                            maxlength={120}
                            placeholder={`Opción ${optionIndex + 1}`}
                            onIonChange={event => updateOption(
                              question.id,
                              optionIndex,
                              event.detail.value || ''
                            )}
                          />
                          <IonButton
                            fill="clear"
                            color="medium"
                            size="small"
                            disabled={question.options.length <= 2}
                            aria-label={`Eliminar opción ${optionIndex + 1}`}
                            onClick={() => updateQuestion(question.id, {
                              options: question.options.filter((_, index) => index !== optionIndex)
                            })}
                          >
                            <IonIcon icon={trashOutline} />
                          </IonButton>
                        </div>
                      ))}
                      <IonButton
                        fill="clear"
                        size="small"
                        disabled={question.options.length >= 10}
                        onClick={() => updateQuestion(question.id, {
                          options: [...question.options, '']
                        })}
                      >
                        <IonIcon slot="start" icon={addOutline} />
                        Agregar opción
                      </IonButton>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="survey-admin-actions">
              <IonButton
                fill="outline"
                disabled={draft.questions.length >= 20}
                onClick={() => setDraft(current => ({
                  ...current,
                  questions: [...current.questions, createSurveyQuestion()]
                }))}
              >
                <IonIcon slot="start" icon={addOutline} />
                Agregar pregunta
              </IonButton>
              <IonButton onClick={saveConfiguration} disabled={isSaving}>
                {isSaving ? <IonSpinner name="crescent" slot="start" /> : <IonIcon slot="start" icon={saveOutline} />}
                Guardar encuesta
              </IonButton>
            </div>
          </IonCardContent>
        </IonCard>

        <IonCard className="survey-results-card">
          <IonCardHeader>
            <div className="survey-card-heading">
              <IonCardTitle>Resultados</IonCardTitle>
              <IonBadge color="primary">{responses.length} respuestas</IonBadge>
            </div>
          </IonCardHeader>
          <IonCardContent>
            {!survey && <p>Guarde la encuesta para comenzar a recibir respuestas.</p>}
            {survey && responses.length === 0 && <p>Aún no se han recibido respuestas.</p>}
            {results.map((result, index) => (
              <div className="survey-result-question" key={result.questionId}>
                <h3>{index + 1}. {result.prompt}</h3>
                <IonNote>{result.answeredCount} respuestas</IonNote>
                {result.options.map(option => (
                  <div className="survey-result-option" key={option.option}>
                    <div className="survey-result-label">
                      <span>{option.option}</span>
                      <strong>{option.count} · {option.percentage}%</strong>
                    </div>
                    <div className="survey-result-track">
                      <div className="survey-result-fill" style={{ width: `${option.percentage}%` }} />
                    </div>
                  </div>
                ))}
                {result.textAnswers.length > 0 && (
                  <IonList className="survey-text-results">
                    {result.textAnswers.map((answer, answerIndex) => (
                      <IonItem key={`${result.questionId}_${answerIndex}`}>
                        <IonLabel className="ion-text-wrap">{answer}</IonLabel>
                      </IonItem>
                    ))}
                  </IonList>
                )}
              </div>
            ))}
          </IonCardContent>
        </IonCard>

        <IonToast
          isOpen={Boolean(toastMessage)}
          message={toastMessage}
          duration={3500}
          position="top"
          onDidDismiss={() => setToastMessage('')}
        />
      </div>
    );
  }

  if (!survey?.active) return null;

  return (
    <div className="session-survey">
      <IonCard className="survey-form-card">
        <IonCardHeader>
          <IonCardTitle>{survey.title}</IonCardTitle>
          {survey.description && <p>{survey.description}</p>}
          {hasSubmitted && <IonBadge color="success">Respondida</IonBadge>}
        </IonCardHeader>
        <IonCardContent>
          {survey.questions.map((question, index) => (
            <div className="survey-form-question" key={question.id}>
              <h3>
                {index + 1}. {question.prompt}
                {question.required && <span className="survey-required"> *</span>}
              </h3>

              {question.type === 'single' && (
                <IonRadioGroup
                  value={answers[question.id] || ''}
                  onIonChange={event => setAnswer(question.id, event.detail.value)}
                >
                  {question.options.map(option => (
                    <IonItem lines="none" key={option}>
                      <IonRadio slot="start" value={option} disabled={isResponseLocked} />
                      <IonLabel className="ion-text-wrap">{option}</IonLabel>
                    </IonItem>
                  ))}
                </IonRadioGroup>
              )}

              {question.type === 'multiple' && question.options.map(option => (
                <IonItem lines="none" key={option}>
                  <IonCheckbox
                    slot="start"
                    disabled={isResponseLocked}
                    checked={Array.isArray(answers[question.id]) &&
                      (answers[question.id] as string[]).includes(option)}
                    onIonChange={event => toggleMultipleAnswer(
                      question.id,
                      option,
                      event.detail.checked
                    )}
                  />
                  <IonLabel className="ion-text-wrap">{option}</IonLabel>
                </IonItem>
              ))}

              {question.type === 'text' && (
                <IonItem lines="none">
                  <IonTextarea
                    value={String(answers[question.id] || '')}
                    disabled={isResponseLocked}
                    maxlength={2000}
                    autoGrow
                    placeholder="Escriba su respuesta"
                    onIonChange={event => setAnswer(question.id, event.detail.value || '')}
                  />
                </IonItem>
              )}
            </div>
          ))}

          <IonButton expand="block" onClick={submitAnswers} disabled={isResponseLocked}>
            {(isSubmitting || isLoadingOwnResponse) && <IonSpinner name="crescent" slot="start" />}
            {hasSubmitted
              ? 'Respuesta enviada'
              : isLoadingOwnResponse
                ? 'Verificando respuesta'
                : 'Enviar respuestas'}
          </IonButton>
          {hasSubmitted && (
            <IonNote color="success">
              Su respuesta quedó registrada y ya no puede modificarse.
            </IonNote>
          )}
          <IonNote className="survey-privacy-note">
            Sus respuestas se guardan para esta sesión. Los resultados solo son visibles para APOYO LOGISTICO.
          </IonNote>
        </IonCardContent>
      </IonCard>

      <IonToast
        isOpen={Boolean(toastMessage)}
        message={toastMessage}
        duration={3500}
        position="top"
        onDidDismiss={() => setToastMessage('')}
      />
    </div>
  );
};

export default SessionSurvey;
